import React, { useState } from "react";
import { useUser } from "@/context/UserContext";
import { deleteTag } from "@/services/TagsService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import CustomSvg from "@/components/common/custom-svg/custom-svg";
import ConfirmDeleteIcon from "@/assets/icons-v2/confirm_delete.png";
import DeleteSuccessAnimation from "./CustomFeed/components/DeleteSuccessAnimation";
import ConfirmationModal from "@/components/common/ConfirmationModal";

interface Tag {
    id: string;
    name: string;
}

interface DeleteTagProps {
    tag?: Tag | null;
    propertyId?: string;
    onSuccess?: (deleted: boolean) => void;
    onCancel?: () => void;
    open?: boolean;
}

const DeleteTag: React.FC<DeleteTagProps> = ({ tag, propertyId, onSuccess, onCancel, open }) => {
    const { userState } = useUser();
    const queryClient = useQueryClient();
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

    const deleteTagMutation = useMutation({
        mutationFn: () => {
            const userId = userState?.id as string | undefined;
            const tagId = tag?.id as string | undefined;
            if (!userId || !propertyId || !tagId) {
                throw new Error("Missing identifiers for tag deletion");
            }
            return deleteTag(userId, propertyId, tagId);
        },
        onSuccess: () => {
            if (propertyId) {
                queryClient.invalidateQueries({ queryKey: ["propertyTags", propertyId] });
                queryClient.invalidateQueries({ queryKey: ["live-variant-listingPage-property", propertyId] });
            }
            queryClient.invalidateQueries({ queryKey: ["live-variant-listingFeed-properties"] });
            setShowDeleteSuccess(true);
            if (onSuccess) onSuccess(true);
        },
        onError: (error: any) => {
            console.error("Error deleting tag:", error);
            toast.error(error?.message || "Failed to delete tag");
            if (onCancel) onCancel();
        },
    });

    const handleConfirmDelete = () => {
        if (tag && propertyId) {
            deleteTagMutation.mutate();
        } else {
            toast.error("Unable to delete tag. Missing property ID.");
            if (onCancel) onCancel();
        }
    };

    const handleCancelDelete = () => {
        if (onCancel) onCancel();
    };

    return (
        <>
            {/* Delete Confirmation Modal */}
            {open && !showDeleteSuccess && (
                <ConfirmationModal
                    show={!!open}
                    title="Delete"
                    icon={<CustomSvg src={ConfirmDeleteIcon} size={{ width: 59, height: 59 }} />}
                    message={
                        <>
                            You sure want to Delete{" "}
                            <span className="tw-font-bold tw-italic tw-text-[#6C26C4]">'#{tag?.name}'</span> tag?
                        </>
                    }
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                    confirmLabel="Delete"
                    cancelLabel="No, Keep it"
                    loading={deleteTagMutation.isPending}
                />
            )}
            {/* Delete Success Animation */}
            {showDeleteSuccess && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-30 tw-z-50 tw-flex tw-items-end md:tw-items-center tw-justify-center">
                    <div className="tw-w-full tw-max-w-md tw-bg-white tw-rounded-t-2xl md:tw-rounded-2xl tw-shadow-lg tw-px-4 tw-py-8 tw-flex tw-flex-col tw-items-center tw-mx-auto tw-min-h-[350px]">
                        <DeleteSuccessAnimation
                            show={showDeleteSuccess}
                            onHide={() => {
                                setShowDeleteSuccess(false);
                                if (onCancel) onCancel();
                            }}
                            message={
                                <>
                                    <span className="tw-italic tw-text-[#6C26C4]">'#{tag?.name}'</span>{" "}
                                    <span className="tw-font-normal">tag</span> Deleted
                                </>
                            }
                            onDone={() => {
                                setShowDeleteSuccess(false);
                                if (onCancel) onCancel();
                            }}
                            buttonLabel="Continue"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default DeleteTag;
